import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;
    const mobileSources = this.refs.videoSourcesMobile || [];

    // When a mobile video is provided and we're on a small viewport, load the
    // mobile sources instead of the desktop ones. Decided once at load time —
    // a background video doesn't need to swap on resize. Falls back to desktop.
    const useMobile = mobileSources.length > 0 && window.matchMedia('(max-width: 749px)').matches;
    const activeSources = useMobile ? mobileSources : videoSources;

    // Clear every candidate, then point the browser at only the active set
    // (a <source> with no src attribute is ignored).
    for (const source of [...videoSources, ...mobileSources]) {
      source.removeAttribute('src');
    }

    for (const source of activeSources) {
      const url = source.dataset.videoSource || source.dataset.videoSourceMobile;

      if (url) source.setAttribute('src', url);
    }

    // Chrome requires both the `muted` property (not just the attribute) and
    // an explicit `.play()` call to autoplay videos whose <source> is
    // populated after initial parse.
    videoElement.muted = true;
    videoElement.playsInline = true;
    videoElement.load();

    const playPromise = videoElement.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // Autoplay blocked (rare with muted videos). Retry after first
        // user interaction so the video starts as soon as possible.
        const resume = () => {
          videoElement.play().catch(() => {});
          document.removeEventListener('pointerdown', resume);
          document.removeEventListener('keydown', resume);
        };
        document.addEventListener('pointerdown', resume, { once: true });
        document.addEventListener('keydown', resume, { once: true });
      });
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
